import { getTextContentURL } from '../components/Content/helper';
import { ContentSchema, ContentsSchema } from '../definitions/schemas';
import ContentInterface from '../graphql/Content/ContentInterface';
import ContentType from '../enums/ContentType';
import OrganizationsStore from '../stores/observables/OrganizationsStore';
import { StorageProviderMTPickers } from '../enums/Integrations/StorageProviders';
import { isInternalRoom, RoomAudience } from '../enums/RoomAudience';

export const canChangeDownloadable = (
  content: Partial<ContentSchema>,
  audience: RoomAudience = RoomAudience.External
) => {
  if (
    content.mtData?.provider === StorageProviderMTPickers.AssetHub &&
    !isInternalRoom({ audience })
  ) {
    return true;
  }

  return [
    ContentType.AUDIO,
    ContentType.DOCUMENT,
    ContentType.IMAGE,
    ContentType.VIDEO,
  ].includes(content.type as ContentType);
};

export const filterTextContents = (contents: ContentsSchema): ContentsSchema =>
  contents.filter((content) => {
    if (content.type !== ContentType.TEXT) {
      return true;
    }

    const [, isUrl] = getTextContentURL(content.text?.text);
    return isUrl;
  });

export const getContentDownloadable = (
  content: Partial<ContentSchema>
): boolean => {
  let downloadable: boolean | undefined;

  switch (content.type) {
    case ContentType.AUDIO:
      downloadable = content.audio?.downloadable;
      break;
    case ContentType.DOCUMENT:
      downloadable = content.document?.downloadable;
      break;
    case ContentType.IMAGE:
      downloadable = content.image?.downloadable;
      break;
    case ContentType.VIDEO:
      downloadable = content.video?.downloadable;
      break;
  }

  return downloadable === true;
};

export const getContentVersion = (
  content: ContentSchema,
  version: number
): ContentSchema => {
  switch (content.type) {
    case ContentType.AUDIO: {
      const contentVersion = content.audios?.find(
        (audio) => audio.version === version
      );

      return {
        ...content,
        audio: contentVersion || content.audio,
      };
    }
    case ContentType.DOCUMENT: {
      const contentVersion = content.documents?.find(
        (doc) => doc.version === version
      );

      return {
        ...content,
        document: contentVersion || content.document,
      };
    }
    case ContentType.VIDEO: {
      const contentVersion = content.videos?.find(
        (video) => video.version === version
      );

      return {
        ...content,
        video: contentVersion || content.video,
      };
    }
    default:
      return content;
  }
};

export const uploadContentCover = async (contentId: string, coverFile: File) =>
  new Promise<string>((resolve, reject) => {
    if (!coverFile) {
      reject('Invalid file');
    } else {
      (async () => {
        try {
          const { uri } = await ContentInterface.uploadCover(
            contentId,
            { organizationId: OrganizationsStore.getCurrent() },
            coverFile
          );
          resolve(uri);
        } catch (e) {
          reject('Error uploading cover');
        }
      })();
    }
  });

// Excluding single-use contents to be used on a Library context
export const onlyLibraryContent = (contents: ContentsSchema) =>
  contents.filter((content) => content.libraryVisible);
